<template>
	<div :class="wrapperClass" v-if="paginator">

		<ul class="pagination" v-if="showPagination">

			<li :class="{ 'prev': true, 'disabled': paginator.current_page <= 1 }" v-show="showPrev">
				<a href="#" @click.prevent="paginate(paginator.current_page - 1)" class="page-link"
				   title="previous"
				   :disabled="paginator.current_page <= 1"
				>
					&laquo;
				</a>
			</li>

			<li v-for="link in page_links" :class="link.classes">
				<a href="#" class="page-link" @click.prevent="paginate(link.page)" v-html="link.page"></a>
			</li>

			<li :class="{ 'next': true, 'disabled': ! paginator.hasMorePages }" v-show="showNext">
				<a href="#" @click.prevent="paginate(paginator.current_page + 1)" class="page-link"
				   title="next"
				   :disabled="! paginator.hasMorePages"
				>
					&raquo;
				</a>
			</li>

		</ul>

		<p :class="showingClass" v-if="showShowing">
			{{ paginatorLang.showingText }} <span class="text-semibold">{{ paginator.from }}</span> {{ paginatorLang.separator }} <span class="text-semibold">{{ paginator.to }}</span>
			{{ paginatorLang.ofText }} <span class="text-semibold">{{ paginator.total }}</span>
			{{ paginator.total | inv-pluralize(...paginatorLang.endText) }}
		</p>

	</div>
</template>

<script>
    import _ from 'lodash';

    export default {
        props: {
            wrapperClass: {
                default: 'text-center mt-15 mb-15'
            },
            showingClass: {
                default: 'text-center mt-15'
            },
            page_data: {
                default: null
            },
            showShowing: {
                type: Boolean,
                default: true
            },
            edges: {
                default: 2
            },
            interval: {
                type: Number,
                default: 4
            },
            dots: {
                type: String,
                default: '...'
            },
            lang: {
                default: () => ({})
            },
            showPrevAlways: {
                type: Boolean,
                default: true,
            },
            showNextAlways: {
                type: Boolean,
                default: true,
            }
        },
        computed: {
            paginator() {
                if (! _.isNil(this.page_data)) {
                    let from = this.page_data.from;
                    if (from > this.page_data.total) {
                        from = this.page_data.total;
                    }

                    return {
                        current_page: this.page_data.current_page,
                        prev_page_url: this.page_data.prev_page_url,
                        next_page_url: this.page_data.next_page_url,
                        from: from,
                        perPage: _.defaultTo(this.page_data.perPage, 25),
                        to: this.page_data.to,
                        total: this.page_data.total,
                        last_page: this.page_data.last_page,
                        hasMorePages: (this.page_data.last_page > this.page_data.current_page),
                        total_pages: _.ceil(this.page_data.total / this.page_data.per_page)
                    };
                }

            },
            page_links() {
                if (_.isNil(this.paginator)) {
                    return [];
                }

                return this.getPageLinks();
            },

            startPage() {
                let start = 1;
                if (this.paginator.current_page > this.half) {
                    start = Math.ceil(
                        Math.max(
                            Math.min(
                                (this.paginator.current_page + 2) - this.half,
                                (this.paginator.total_pages - this.interval)
                            ),
                            1
                        )
                    );
                }
                return start;
            },

            half() {
                return this.interval / 2;
            },

            intervalEnd() {
                let end = Math.ceil(Math.min(this.interval, this.paginator.total_pages)) + 2;
                if (this.paginator.current_page > this.half) {
                    end = Math.ceil(Math.min(this.paginator.current_page + this.half), this.paginator.total_pages) + 3;
                }

                if (end > this.paginator.total_pages) {
                    end = this.paginator.total_pages;
                }

                return end;
            },

            /**
             * Calculate the offset.
             */
            offset() {
                return ((this.paginator.current_page - 1) * this.paginator.perPage);
            },

            /**
             * Determine if pagination links should show up.
             */
            showPagination() {
                return this.paginator.total_pages > 1;
            },

            /**
             * Determine if previous link is to be displayed.
             */
            showPrev() {
                if (! this.showPrevAlways && this.paginator.current_page <= 1) {
                    return false;
                }

                return true;
            },

            /**
             * Determine if the next link should be displayed.
             */
            showNext() {
                if (! this.paginator.hasMorePages && ! this.showNextAlways) {
                    return false;
                }

                return true;
            }
        },
        data() {
            return {
                paginatorLang: this.setLang()
            };
        },
        methods: {
            /**
             * Get page links.
             */
            getPageLinks() {
                let page_links = [];
                let count = 0;
                let dots = `<span>${this.dots}</span>`;
                let add_dots = (this.paginator.total_pages - (this.interval + 1)) > this.startPage;

                let begin_count = 1;
                for (let i = 0; i < this.edges; i++) {
                    let begin_classes = [ 'page' ];
                    if (this.paginator.current_page === begin_count) {
                        begin_classes.push('active');
                    }

                    page_links.push({
                        page: begin_count,
                        uid: begin_count,
                        classes: begin_classes
                    });

                    begin_count++;
                }

                if (this.startPage > this.edges) {
                    page_links.push({
                        page: dots,
                        uid: 'dotbegin',
                        classes: [ 'page', 'disabled', 'dots' ]
                    });
                }

                let interval = Math.min(this.interval, this.paginator.total_pages);

                for (let i = this.startPage; i <= this.intervalEnd; i++) {
                    let link_classes = ['page'];
                    if (i === this.paginator.current_page && count < this.interval) {
                        link_classes.push('active');
                    } else if (count === interval) {
                        link_classes.push('disabled dots');
                    }

                    if (count <= interval) {
                        if (count !== interval || (count === interval && add_dots)) {
                            page_links.push({
                                page: (count === interval) ? dots : i,
                                uid: (count === interval) ? 'dotend' : i,
                                classes: link_classes
                            });
                        }
                    }

                    count++;
                }

                count = this.paginator.total_pages;
                let end_links = [];

                for (let i = 0; i < this.edges; i++) {
                    let last_page_classes = [ 'page' ];
                    if (this.paginator.current_page === count) {
                        last_page_classes.push('active');
                    }

                    end_links.push({
                        page: count,
                        uid: count,
                        classes: last_page_classes
                    });

                    count--;
                }

                end_links = _.sortBy(end_links, (link) => {
                    return link.page;
                });

                page_links.push(end_links);
                page_links = _.flatten(page_links);

                return _.uniqWith(page_links, _.isEqual);
            },

            /**
             * Set paginator language.
             */
            setLang() {
                let defaults = {
                    showingText: 'Showing',
                    ofText: 'of',
                    endText: ['', ''],
                    separator: '-'
                };

                let merged = _.merge(defaults, this.lang);

                if (! _.isArray(merged.endText)) {
                    merged.endText = [merged.endText];
                }

                return merged;
            },

            /**
             * Paginate collection.
             *
             * @param {integer} page
             */
            paginate(page) {
                if (this.shouldPaginate(page)) {
                    this.$emit('paginate', page);
                }
            },

            /**
             * Determine if paginator should run.
             *
             * @param {integer} page
             */
            shouldPaginate(page) {
                page = parseInt(page);

                if (_.isNaN(page) || page < 1) {
                    return false;
                }

                if (! _.isNil(this.paginator)) {
                    if (! this.paginator.hasMorePages && page >= this.paginator.last_page) {
                        return false;
                    } else if (this.paginator.hasMorePages && page > this.paginator.last_page) {
                        return false;
                    }
                }

                return true;
            },
        }
    }
</script>